// src/pages/PartnershipPage.jsx
import React, { useState, useEffect } from 'react';
import axios from '../api/axios';

export default function PartnershipPage() {
  const [partnerships, setPartnerships] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [filters, setFilters] = useState({
    category: 'ALL',
    search: ''
  });
  const [selectedPartnership, setSelectedPartnership] = useState(null);
  const [showDetailModal, setShowDetailModal] = useState(false);

  // Helper function to parse tags from different formats
  const parseTags = (tagsString) => {
    if (!tagsString) return [];
    
    // Split by comma first, then by # for each part
    return tagsString
      .split(',')
      .flatMap(part => 
        part.trim().split('#')
          .map(tag => tag.trim())
          .filter(tag => tag.length > 0)
      )
      .filter(tag => tag.length > 0);
  };

  // Category options for filter
  const categoryOptions = [
    { value: 'ALL', label: 'All Categories' },
    { value: 'BAR', label: 'Bar' },
    { value: 'CAFE', label: 'Cafe' },
    { value: 'RESTAURANT', label: 'Restaurant' },
    { value: 'SHOPPING', label: 'Shopping' },
    { value: 'ENTERTAINMENT', label: 'Entertainment' }
  ];

  useEffect(() => {
    fetchPartnerships();
  }, []);

  // ESC 키로 모달 닫기
  useEffect(() => {
    const handleEscape = (e) => {
      if (e.key === 'Escape' && showDetailModal) {
        setShowDetailModal(false);
      }
    };

    if (showDetailModal) {
      document.addEventListener('keydown', handleEscape);
      return () => document.removeEventListener('keydown', handleEscape);
    }
  }, [showDetailModal]);

  const fetchPartnerships = async () => {
    try {
      setLoading(true);
      const response = await axios.get('/api/partnerships');
      setPartnerships(response.data);
    } catch (error) {
      setError('Failed to load partnerships. Please try again later.');
    } finally {
      setLoading(false);
    }
  };

  const handleWebsiteClick = async (partnershipId) => {
    try {
      // 임시로 로컬 상태에서만 클릭 수 증가
      setPartnerships(prev => prev.map(p => 
        p.id === partnershipId 
          ? { ...p, clickCount: (p.clickCount || 0) + 1 }
          : p
      ));
      
      // 실제 API 호출 시도 (실패해도 무시)
      try {
        await axios.post(`/api/partnerships/${partnershipId}/click`);
      } catch (apiError) {
        // Silent fail
      }
    } catch (error) {
      // Silent fail
    }
  };

  const handleView = async (partnershipId) => {
    try {
      // 임시로 로컬 상태에서만 조회 수 증가
      setPartnerships(prev => prev.map(p => 
        p.id === partnershipId 
          ? { ...p, viewCount: (p.viewCount || 0) + 1 }
          : p
      ));
      
      // 실제 API 호출 시도 (실패해도 무시)
      try {
        await axios.post(`/api/partnerships/${partnershipId}/view`);
      } catch (apiError) {
        // Silent fail
      }
    } catch (error) {
      // Silent fail
    }
  };

  const handlePartnershipClick = (partnership) => {
    setSelectedPartnership(partnership);
    setShowDetailModal(true);
    handleView(partnership.id); // 클릭 시 조회수 증가
  };

  // Filter partnerships based on current filters
  const filteredPartnerships = partnerships.filter(partnership => {
    if (!partnership.isActive) return false;
    
    if (filters.category !== 'ALL' && partnership.category !== filters.category) {
      return false;
    }
    
    if (filters.search) {
      const searchLower = filters.search.toLowerCase();
      const tags = parseTags(partnership.tags);
      return (
        partnership.name.toLowerCase().includes(searchLower) ||
        partnership.description.toLowerCase().includes(searchLower) ||
        partnership.benefitTitle.toLowerCase().includes(searchLower) ||
        tags.some(tag => tag.toLowerCase().includes(searchLower))
      );
    }
    
    return true;
  });

  // Sort by display order, then by name
  const sortedPartnerships = filteredPartnerships.sort((a, b) => {
    if (a.displayOrder !== b.displayOrder) {
      return (a.displayOrder || 999) - (b.displayOrder || 999);
    }
    return a.name.localeCompare(b.name);
  });

  if (loading) {
    return (
      <div className="max-w-6xl mx-auto px-4 py-12">
        <div className="flex justify-center items-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="max-w-6xl mx-auto px-4 py-12">
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 text-center">
          <p className="text-red-800">{error}</p>
          <button
            onClick={fetchPartnerships}
            className="mt-2 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700"
          >
            Try Again
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-6xl mx-auto px-4 py-12">
      <div className="text-center mb-12">
        <h1 className="text-4xl font-black text-gray-900 sm:text-5xl mb-4 flex items-center justify-center gap-3">
          Our Partners
        </h1>
        <p className="mt-4 text-xl text-gray-600 mb-8 flex items-center justify-center gap-2">
          <span className="text-xl">✨</span>
        SLAM members get exclusive benefits at these amazing places!
          <span className="text-xl">✨</span>
        </p>
        
        {/* Filters */}
        <div className="flex flex-col sm:flex-row gap-4 justify-center items-center bg-gray-50 rounded-lg p-4 border border-gray-200">
          <div className="flex items-center gap-2">
            <label htmlFor="category-filter" className="text-sm font-medium text-gray-700 flex items-center gap-1">
              <span>🏷️</span>
              Category:
            </label>
            <select
              id="category-filter"
              value={filters.category}
              onChange={(e) => setFilters(prev => ({ ...prev, category: e.target.value }))}
              className="border border-gray-300 rounded-lg px-3 py-1 focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              {categoryOptions.map(option => (
                <option key={option.value} value={option.value}>{option.label}</option>
              ))}
            </select>
          </div>
          <div className="flex items-center gap-2">
            <label htmlFor="search-filter" className="text-sm font-medium text-gray-700 flex items-center gap-1">
              <span>🔍</span>
              Search:
            </label>
            <input
              id="search-filter"
              type="text"
              placeholder="Search partners..."
              value={filters.search}
              onChange={(e) => setFilters(prev => ({ ...prev, search: e.target.value }))}
              className="border border-gray-300 rounded-lg px-3 py-1 focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>
        </div>
      </div>

      {sortedPartnerships.length === 0 ? (
        <div className="text-center py-12">
          <div className="text-6xl mb-4">🔍</div>
          <p className="text-gray-500 text-lg">No partnerships found matching your criteria.</p>
          <p className="text-gray-400 text-sm mt-2">Try adjusting your filters or search terms</p>
        </div>
      ) : (
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
          {sortedPartnerships.map(partnership => (
            <div
              key={partnership.id}
              className="bg-white rounded-lg shadow-md border-2 border-gray-100 p-6 text-center hover:shadow-lg hover:border-blue-200 transition-all duration-300 cursor-pointer space-y-5 md:space-y-6"
              onClick={() => handlePartnershipClick(partnership)}
              onMouseEnter={() => handleView(partnership.id)}
            >
              <img
                src={partnership.logoUrl}
                alt={`${partnership.name} logo`}
                className="h-20 w-20 mx-auto mb-4 rounded-lg object-cover"
                onError={(e) => {
                  e.target.src = '/placeholder-logo.png';
                }}
              />
              <h2 className="text-xl font-semibold mb-2">{partnership.name}</h2>
              <p className="text-gray-600 text-sm mb-5 line-clamp-3">
                {partnership.description.length > 100 
                  ? `${partnership.description.substring(0, 100)}...` 
                  : partnership.description}
              </p>
              <p className="text-xs text-blue-500 mb-4 cursor-pointer hover:text-blue-700">
                Click for more details →
              </p>
              
              <div className="mb-4">
                <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                  {partnership.category}
                </span>
              </div>
              
              <div className="mb-6">
                <h3 className="text-lg font-semibold text-blue-600 mb-3 whitespace-pre-line">{partnership.benefitTitle}</h3>
                <p className="text-sm text-gray-700 whitespace-pre-line mt-1">{partnership.benefitDescription}</p>
                {partnership.benefitType === 'DISCOUNT' && partnership.discountPercentage && (
                  <p className="text-lg font-bold text-green-600 mt-1">
                    {partnership.discountPercentage}% OFF
                  </p>
                )}
              </div>
              
              <div className="text-sm text-gray-500 mb-4">
                {partnership.operatingHours && (
                  <div className="mb-3">
                    <span className="text-gray-500 mr-1">🕒</span>
                    <span className="whitespace-pre-line text-xs">
                      {partnership.operatingHours.length > 50 
                        ? `${partnership.operatingHours.substring(0, 50)}...` 
                        : partnership.operatingHours}
                    </span>
                  </div>
                )}
                {partnership.phoneNumber && (
                  <p className="text-xs mt-2">📞 {partnership.phoneNumber}</p>
                )}
              </div>
              
              {partnership.websiteUrl && (
                <a
                  href={partnership.websiteUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  onClick={(e) => {
                    e.stopPropagation(); // 모달이 열리지 않도록 이벤트 전파 중단
                    handleWebsiteClick(partnership.id);
                  }}
                  className="inline-block bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors"
                >
                  Visit Website
                </a>
              )}
              
              {partnership.tags && (
                <div className="mt-3 flex flex-wrap justify-center gap-1">
                  {parseTags(partnership.tags).slice(0, 3).map((tag, index) => {
                    const tagColors = [
                      'bg-blue-100 text-blue-800',
                      'bg-green-100 text-green-800', 
                      'bg-purple-100 text-purple-800'
                    ];
                    return (
                      <span
                        key={index}
                        className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${tagColors[index] || 'bg-gray-100 text-gray-800'}`}
                      >
                        {tag}
                      </span>
                    );
                  })}
                </div>
              )}
          </div>
        ))}
      </div>
      )}

      {/* Partnership Detail Modal */}
      {showDetailModal && selectedPartnership && (
        <div 
          className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4"
          onClick={() => setShowDetailModal(false)}
        >
          <div 
            className="bg-white rounded-lg max-w-2xl w-full max-h-[90vh] overflow-y-auto"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="p-6">
              {/* Header */}
              <div className="flex justify-between items-start mb-6">
                <div className="flex items-center">
                  <img
                    src={selectedPartnership.logoUrl}
                    alt={`${selectedPartnership.name} logo`}
                    className="h-16 w-16 rounded-lg object-cover mr-4"
                    onError={(e) => {
                      e.target.src = '/placeholder-logo.png';
                    }}
                  />
                  <div>
                    <h2 className="text-2xl font-bold text-gray-900">{selectedPartnership.name}</h2>
                    <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                      {selectedPartnership.category}
                    </span>
                  </div>
                </div>
                <button
                  onClick={() => setShowDetailModal(false)}
                  className="text-gray-400 hover:text-gray-600 text-2xl"
                >
                  ×
                </button>
              </div>

              {/* Description */}
              <div className="mb-6">
                <h3 className="text-lg font-semibold text-gray-900 mb-2">About</h3>
                <p className="text-gray-700 whitespace-pre-line leading-relaxed">
                  {selectedPartnership.description}
                </p>
              </div>

              {/* Benefit Information */}
              <div className="mb-6">
                <h3 className="text-lg font-semibold text-gray-900 mb-2">SLAM Member Benefits</h3>
                <div className="bg-blue-50 rounded-lg p-4">
                  <h4 className="text-lg font-semibold text-blue-600 mb-3 whitespace-pre-line">{selectedPartnership.benefitTitle}</h4>
                  <p className="text-gray-700 mb-2 mt-1 whitespace-pre-line leading-relaxed">
                    {selectedPartnership.benefitDescription}
                  </p>
                  {selectedPartnership.benefitType === 'DISCOUNT' && selectedPartnership.discountPercentage && (
                    <div className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-green-100 text-green-800">
                      {selectedPartnership.discountPercentage}% OFF
                    </div>
                  )}
                </div>
              </div>

              {/* Contact Information */}
              <div className="mb-6">
                <h3 className="text-lg font-semibold text-gray-900 mb-2">Contact Information</h3>
                <div className="space-y-2">
                  <div className="flex items-center">
                    <span className="text-gray-500 mr-2">📍</span>
                    <span className="text-gray-700">{selectedPartnership.address}</span>
                  </div>
                  {selectedPartnership.phoneNumber && (
                    <div className="flex items-center">
                      <span className="text-gray-500 mr-2">📞</span>
                      <a 
                        href={`tel:${selectedPartnership.phoneNumber}`}
                        className="text-blue-600 hover:text-blue-800"
                      >
                        {selectedPartnership.phoneNumber}
                      </a>
                    </div>
                  )}
                  {selectedPartnership.operatingHours && (
                    <div className="flex items-start">
                      <span className="text-gray-500 mr-2 mt-1">🕒</span>
                      <div className="text-gray-700 whitespace-pre-line">
                        {selectedPartnership.operatingHours}
                      </div>
                    </div>
                  )}
                </div>
              </div>

              {/* Tags */}
              {selectedPartnership.tags && (
                <div className="mb-6">
                  <h3 className="text-lg font-semibold text-gray-900 mb-2">Tags</h3>
                  <div className="flex flex-wrap gap-2">
                    {parseTags(selectedPartnership.tags).map((tag, index) => {
                      const tagColors = [
                        'bg-blue-100 text-blue-800',
                        'bg-green-100 text-green-800', 
                        'bg-purple-100 text-purple-800',
                        'bg-yellow-100 text-yellow-800',
                        'bg-pink-100 text-pink-800',
                        'bg-indigo-100 text-indigo-800'
                      ];
                      return (
                        <span
                          key={index}
                          className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${tagColors[index % tagColors.length]}`}
                        >
                          {tag}
                        </span>
                      );
                    })}
                  </div>
                </div>
              )}


              {/* Action Buttons */}
              <div className="flex gap-3">
                {selectedPartnership.websiteUrl && (
                  <a
                    href={selectedPartnership.websiteUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    onClick={() => handleWebsiteClick(selectedPartnership.id)}
                    className="flex-1 bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors text-center"
                  >
                    Visit Website
                  </a>
                )}
                {selectedPartnership.phoneNumber && (
                  <a
                    href={`tel:${selectedPartnership.phoneNumber}`}
                    className="flex-1 bg-green-600 text-white px-4 py-2 rounded-lg hover:bg-green-700 transition-colors text-center"
                  >
                    Call Now
                  </a>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}